import { Lock, Flame, ArrowRight } from 'lucide-react';
import { startUpgrade } from '@/lib/billing';

const PREMIUM_CHALLENGES = [
  {
    length: 14,
    label: '14-day streak',
    tagline: 'Commit to learning',
    blurb: 'A two-week sprint to anchor your new self-belief.',
  },
  {
    length: 21,
    label: '21-day streak',
    tagline: 'Push the limits',
    blurb: 'Three weeks to fully rewire your inner narrative.',
  },
  {
    length: 30,
    label: '30-day streak',
    tagline: 'Anchor it for life',
    blurb: 'A full month to make the shift second nature.',
  },
];

/**
 * Compact upsell strip listing the locked Premium challenges (14 / 21 day)
 * with a unified $1 / 7-day trial CTA. Used on /confidence/library so lead
 * users can see what they're unlocking even after they've started the free
 * 7-day plan.
 */
export default function PremiumChallengesUpsell() {
  return (
    <div
      className="mb-10 p-5 rounded-2xl bg-gradient-to-br from-amber-500/10 via-zinc-900/60 to-violet-500/10 border border-amber-500/20"
      data-testid="premium-challenges-upsell"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-amber-300/80 mb-1">
            Premium Challenges
          </p>
          <h3 className="text-lg font-serif text-white">Go deeper — Premium</h3>
        </div>
        <button
          onClick={() => startUpgrade('trial')}
          className="inline-flex items-center justify-center gap-1.5 bg-white text-zinc-950 hover:bg-zinc-100 font-semibold text-sm px-4 py-2.5 rounded-full transition-colors shrink-0"
          data-testid="premium-upsell-cta"
        >
          Start $1 trial
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        {PREMIUM_CHALLENGES.map((ch) => (
          <button
            key={ch.length}
            type="button"
            onClick={() => startUpgrade('trial')}
            className="group flex items-center gap-4 p-4 rounded-xl bg-zinc-900/60 border border-white/5 hover:border-amber-500/30 transition-colors text-left"
            data-testid={`premium-challenge-${ch.length}`}
          >
            {/* Hex badge */}
            <div className="relative w-12 h-14 flex items-center justify-center shrink-0" aria-hidden>
              <svg viewBox="0 0 64 72" className="absolute inset-0 w-full h-full">
                <polygon
                  points="32,2 60,18 60,54 32,70 4,54 4,18"
                  fill="rgba(251,191,36,0.15)"
                  stroke="#fbbf24"
                  strokeWidth="2"
                />
              </svg>
              <div className="relative flex items-center gap-1 z-10">
                <Flame className="w-3 h-3 text-amber-400" />
                <span className="text-xs font-bold text-amber-300">{ch.length}</span>
              </div>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <p className="text-sm font-medium text-white">{ch.label}</p>
                <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-full border text-amber-300 border-amber-500/30 bg-amber-500/10">
                  Premium · $1
                </span>
              </div>
              <p className="text-xs text-zinc-500 leading-snug">{ch.blurb}</p>
            </div>

            {/* Lock + arrow */}
            <div className="flex flex-col items-center gap-1 text-zinc-500 group-hover:text-amber-300 transition-colors">
              <Lock className="w-4 h-4" />
            </div>
          </button>
        ))}
      </div>

      <p className="text-[11px] text-zinc-500 mt-3 text-center sm:text-left">
        $1 for 7 days · then $7/month · cancel anytime
      </p>
    </div>
  );
}
